import { useMutation } from 'react-query';
import { Http } from '../../services/Http';
import { MonthlyTimesheet, TimesheetStatus } from '../../types';
import { MUTATION_KEYS } from '../constants';
import { useInvalidateMonthlyTimesheets } from './useMonthlyTimesheets';
import { useInvalidateMonthlySingleTimesheet } from './useMonthlySingleTimesheet';

type SubmitPayload = {
  status: TimesheetStatus;
  ids: number[];
  // Called after every batch completes so the UI can render approval progress.
  onProgress?: (completedBatches: number, totalBatches: number) => void;
};

// Approving many timesheets in a single request makes the backend process every item
// synchronously, which can exceed the API gateway timeout and fail with a 502 for deal
// owners with a large backlog. Splitting the selection into smaller sequential batches keeps
// each request short and well within the timeout.
export const BULK_APPROVE_CHUNK_SIZE = 5;

function chunk<T>(items: T[], size: number): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
}

async function approveMontlyTimesheets({ ids, status, onProgress }: SubmitPayload): Promise<MonthlyTimesheet | void> {
  const batches = chunk(ids, BULK_APPROVE_CHUNK_SIZE);
  let lastData: MonthlyTimesheet | undefined;
  let completed = 0;
  onProgress?.(0, batches.length);
  for (const batch of batches) {
    const { data } = await Http.axios.patch<SubmitPayload, MonthlyTimesheet>(
      `/monthly-timesheet/bulk-update`,
      {
        status,
        ids: batch,
      }
    );
    lastData = data;
    completed += 1;
    onProgress?.(completed, batches.length);
  }
  return lastData;
}
export const useBulkApproveMontlyTimesheet = () => {
  const invalidateMontlyTimesheets = useInvalidateMonthlyTimesheets();
  const invalidateMontlySingleTimesheet = useInvalidateMonthlySingleTimesheet();

  return useMutation(approveMontlyTimesheets, {
    mutationKey: MUTATION_KEYS.APPROVE_MONTLY_TIMESHEET,
    onSuccess: () => {
      invalidateMontlyTimesheets();
      invalidateMontlySingleTimesheet();
    },
  });
};
